<template>
  <v-dialog persistent v-model="dialog" max-width="640">
    <v-card v-if="item" class="activar-tarjeta">
      <div class="activar-tarjeta__cabecera">
        <v-icon color="white" size="30" class="mr-3">mdi-gift-outline</v-icon>
        <div class="activar-tarjeta__titulo">
          <strong>{{ activada ? "Tarjeta activada" : "Activar tarjeta de regalo" }}</strong>
          <small v-if="item.invoice && item.invoice.name">Vendida en la factura {{ item.invoice.name }}</small>
        </div>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="close()" :title="activada ? 'Cerrar' : 'Cancelar'">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Listo: el código a la vista y a un clic de imprimirla -->
      <v-card-text v-if="activada" class="activar-tarjeta__cuerpo text-center">
        <v-icon size="54" color="success">mdi-check-circle-outline</v-icon>
        <p class="activar-tarjeta__listo">Ya se puede usar. Entrégale este código al cliente:</p>
        <div class="activar-tarjeta__codigo-grande">{{ activada.code }}</div>
        <div class="activar-tarjeta__resumen-chips">
          <v-chip small label color="success" text-color="white">Saldo {{ $filters.currency(item.amount, 0) }}</v-chip>
          <v-chip small label outlined>{{ activada.vence ? "Vence el " + activada.vence : "Sin vencimiento" }}</v-chip>
        </div>
        <v-btn large depressed color="primary" class="mt-5" :loading="imprimiendo" @click="imprimir()">
          <v-icon left>mdi-printer-outline</v-icon>Imprimir la tarjeta (PDF)
        </v-btn>
        <p class="activar-tarjeta__nota">El PDF solo se puede sacar durante los primeros 30 minutos después de activarla.</p>
        <v-btn text color="primary" class="mt-1" @click="close()">Listo</v-btn>
      </v-card-text>

      <v-card-text v-else class="activar-tarjeta__cuerpo">
        <!-- Qué se está activando -->
        <div class="activar-tarjeta__saldo">
          <span>Saldo que recibe la tarjeta</span>
          <strong>{{ $filters.currency(item.amount, 0) }}</strong>
        </div>

        <!-- 1. Código -->
        <h3 class="activar-tarjeta__paso"><span>1</span>Código de la tarjeta</h3>
        <v-text-field
          v-model="codigo"
          outlined
          dense
          hide-details="auto"
          autocomplete="off"
          maxlength="40"
          placeholder="Escribe el código de la tarjeta"
          prepend-inner-icon="mdi-barcode-scan"
          :rules="[reglaCodigo]"
          @input="normalizarCodigo"
          class="activar-tarjeta__campo-codigo"
        ></v-text-field>
        <div class="activar-tarjeta__ayuda">
          Si ya tienes la tarjeta impresa, escribe o escanea su código. Si no, deja que el sistema cree uno.
        </div>
        <v-btn outlined color="primary" class="mt-2" :loading="loadingCode" @click="generateCode()">
          <v-icon left>mdi-auto-fix</v-icon>Generar código automáticamente
        </v-btn>

        <!-- 2. Vencimiento -->
        <h3 class="activar-tarjeta__paso"><span>2</span>¿Hasta cuándo se puede usar?</h3>
        <v-chip-group v-model="vigencia" mandatory active-class="activar-tarjeta__chip-activo" class="activar-tarjeta__vigencias">
          <v-chip v-for="o in opcionesVigencia" :key="o.clave" :value="o.clave" outlined>{{ o.texto }}</v-chip>
        </v-chip-group>
        <div v-if="vigencia === 'fecha'" class="mt-3">
          <ng-date v-model="fechaPropia" label="Fecha de vencimiento" hide-details></ng-date>
        </div>
        <div class="activar-tarjeta__ayuda" v-if="vencimiento">
          Vence el <b>{{ vencimientoTexto }}</b> ({{ duracionTexto }}).
        </div>
        <div class="activar-tarjeta__ayuda" v-else-if="vigencia !== 'fecha'">La tarjeta no se vence: el saldo se puede gastar cuando el cliente quiera.</div>
      </v-card-text>

      <v-card-actions v-if="!activada" class="activar-tarjeta__pie">
        <v-btn text color="secondary" @click="close()">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn large depressed color="primary" :disabled="!puedeActivar" :loading="activando" @click="activateGiftCard()">
          <v-icon left>mdi-credit-card-check-outline</v-icon>Activar tarjeta
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

const OPCIONES_VIGENCIA = [
  { clave: "ninguna", texto: "Sin vencimiento" },
  { clave: "3", texto: "3 meses", meses: 3 },
  { clave: "6", texto: "6 meses", meses: 6 },
  { clave: "12", texto: "1 año", meses: 12 },
  { clave: "fecha", texto: "Otra fecha" },
];

export default {
  data() {
    return {
      dialog: false,
      item: null,
      codigo: "",
      vigencia: "ninguna",
      fechaPropia: null,
      loadingCode: false,
      activando: false,
      imprimiendo: false,
      activada: null,
      opcionesVigencia: OPCIONES_VIGENCIA,
    };
  },
  computed: {
    // YYYY-MM-DD o null (sin vencimiento).
    vencimiento() {
      if (this.vigencia === "fecha") {
        return this.fechaPropia ? moment(this.fechaPropia).format("YYYY-MM-DD") : null;
      }
      const o = OPCIONES_VIGENCIA.find((x) => x.clave === this.vigencia);
      return o && o.meses ? moment().add(o.meses, "months").format("YYYY-MM-DD") : null;
    },
    vencimientoTexto() {
      return this.vencimiento ? moment(this.vencimiento).format("DD/MM/YYYY") : "";
    },
    duracionTexto() {
      const meses = Math.round(moment(this.vencimiento).diff(moment(), "months", true));
      if (meses < 1) {
        return "menos de un mes";
      }
      return meses === 1 ? "1 mes" : meses + " meses";
    },
    codigoValido() {
      return this.codigo.trim().length >= 6;
    },
    puedeActivar() {
      return this.codigoValido && (this.vigencia !== "fecha" || !!this.fechaPropia);
    },
  },
  methods: {
    renderItem(item) {
      this.item = item;
      this.codigo = "";
      this.vigencia = "ninguna";
      this.fechaPropia = null;
      this.activada = null;
      this.dialog = true;
    },
    close() {
      this.item = null;
      this.dialog = false;
      this.activada = null;
    },
    // El código se escribe en mayúsculas y sin espacios: así se busca después en la caja.
    normalizarCodigo(valor) {
      const limpio = String(valor || "").toUpperCase().replace(/\s+/g, "");
      if (limpio !== valor) {
        this.$nextTick(() => {
          this.codigo = limpio;
        });
      }
    },
    reglaCodigo(valor) {
      return !valor || String(valor).trim().length >= 6 || "Mínimo 6 caracteres";
    },
    async generateCode() {
      this.loadingCode = true;
      const res = await this.$http.get("api/v1/gift-card/generate-code", null, false, true);
      this.loadingCode = false;
      if (res.success) {
        this.codigo = res.code;
      }
    },
    getValues() {
      const vence = this.vencimiento;
      return {
        id: this.item.id,
        code: this.codigo.trim(),
        has_expiration: !!vence,
        expiration_date: vence || undefined,
      };
    },
    async activateGiftCard() {
      if (!this.puedeActivar || this.activando) {
        return;
      }
      const datos = this.getValues();
      this.activando = true;
      const res = await this.$http.post("api/v1/gift-card/activate", datos, true, true);
      this.activando = false;
      if (res.success) {
        this.activada = { id: datos.id, code: datos.code, vence: this.vencimientoTexto };
        this.$emit("refresh");
      }
    },
    async imprimir() {
      this.imprimiendo = true;
      // Se abre la ventana en el clic (si no, el navegador bloquea la que se abre después de esperar al servidor).
      const ventana = window.open("", "_blank");
      const res = await this.$http.get("api/v1/gift-card/" + this.activada.id + "/pdf", null, true, true);
      this.imprimiendo = false;
      if (res && res.success) {
        const url = this.$http.getBaseUrlPublicFiles() + "tmp/" + res.item;
        if (ventana) {
          ventana.location.href = url;
        } else {
          window.open(url);
        }
      } else if (ventana) {
        ventana.close();
      }
    },
  },
};
</script>

<style scoped>
.activar-tarjeta__cabecera {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: var(--pos-encabezado);
  color: #fff;
}
.activar-tarjeta__titulo {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.activar-tarjeta__titulo strong {
  font-size: 18px;
}
.activar-tarjeta__titulo small {
  opacity: 0.9;
}
.activar-tarjeta__cuerpo {
  padding: 20px 24px 8px !important;
}
.activar-tarjeta__saldo {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 14px 18px;
  border-radius: 10px;
  background: #e6f6ec;
  color: #1b5e20;
}
.activar-tarjeta__saldo strong {
  font-size: 28px;
}
.activar-tarjeta__paso {
  display: flex;
  align-items: center;
  margin: 24px 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #3c3c3b;
}
.activar-tarjeta__paso span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 8px;
  border-radius: 50%;
  background: #24a9e1;
  color: #fff;
  font-size: 12px;
}
.activar-tarjeta__campo-codigo >>> input {
  font-family: "Courier New", monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
}
.activar-tarjeta__ayuda {
  margin-top: 6px;
  font-size: 12.5px;
  color: #5f6368;
}
.activar-tarjeta__vigencias >>> .v-chip {
  margin-right: 8px;
}
.activar-tarjeta__campo-codigo >>> input::placeholder {
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
}
.activar-tarjeta__pie {
  padding: 12px 24px 20px !important;
}
.activar-tarjeta__listo {
  margin: 10px 0 14px;
  font-size: 15px;
  color: #3c3c3b;
}
.activar-tarjeta__codigo-grande {
  display: inline-block;
  padding: 14px 26px;
  border: 2px dashed #24a9e1;
  border-radius: 12px;
  background: #f1f9fd;
  font-family: "Courier New", monospace;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 4px;
  color: var(--pos-azul-oscuro);
  word-break: break-all;
}
.activar-tarjeta__resumen-chips {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 14px;
}
.activar-tarjeta__nota {
  margin: 10px 0 0;
  font-size: 12px;
  color: #5f6368;
}
</style>

<style>
/* La vigencia escogida se pinta llena (sin esto, el chip «outlined» de Vuetify la deja transparente). */
.v-chip.v-chip--outlined.v-chip.v-chip.activar-tarjeta__chip-activo {
  background-color: #24a9e1 !important;
  border-color: #24a9e1 !important;
  color: #fff !important;
}
</style>
